import {DropdownInput, Fieldset, IconAndText, Text} from "atmosfair-ui";
import AutocompleteQuery, {
    AUTOCOMPLETE_ACTIVITY_TYPE,
    AUTOCOMPLETE_QUERY_TYPE
} from "../../../molecules/autocomplete-query/AutocompleteQuery";
import {isCustomer, isCustomerCompanyResponse} from "../../../utils/check/type-guards";
import React, {useEffect, useRef, useState} from "react";
import useFirstRender from "../../../hooks/use-first-render";
import {AgencyProps} from "../../../api/model/agency";
import {Customer, CustomerCompany} from "../../../api/model/customer";
import {theme} from "../../../styles/theme";
import clsx from "clsx";
import AddressForm from "../address-form/AddressForm";
import PhoneNumberForm from "../phone-number-form/PhoneNumberForm";
import {validateCompanyName, validateEmail, validateFirstAndLastName} from "../utils";
import {convertAgenciesFromDropdownFormat, convertAgenciesToDropdownFormat} from "../../../utils/converter/dropdown";
import {isEqual} from "lodash";
import {VALIDATE_FORM_STARTING_NUMBER} from "../../../constants/default-values";
import {useEffectAllDepsChange} from "../../../hooks/custom-use-effect";
import "./styles.css"

export interface CustomerFormProps {
    agencies: AgencyProps[],
    defaultCustomer: Customer | null,
    validateForm: number,
    showCompany: boolean,
    hideAgencySelection: boolean,
    onFormIsValid: (data: Customer | null) => void,
    selectedAgency?: AgencyProps | null,
}

export interface CustomCustomerFormProps {
    id: number | null,
    firstName: string,
    lastName: string,
    company: CustomerCompany | null,
    email: string,
}

export const initialCustomer: CustomCustomerFormProps = {
    id: null,
    firstName: '',
    lastName: '',
    company: null,
    email: '',
}

export const initialAddress = {
    street: "",
    houseNumber: "",
    city: "",
    zipCode: "",
    additionalAddressInformation: ""
}

export const initialPhoneNumber = {
    phoneNumber: ""
}

export const completeInitialCustomer = {
    ...initialCustomer,
    ...initialAddress,
    ...initialPhoneNumber
}

interface SaveTravelComponentsDialogueFormErrorProps {
    firstNameError: boolean,
    lastNameError: boolean,
    emailError: boolean,
    companyNameError: boolean,
    agencyError: boolean,
}

interface FreelyTypedCustomerProps extends Pick<Customer, 'firstName' | 'lastName' | 'company' | 'email'> {
    id: null
}

interface CustomerAddressProps {
    street: string,
    houseNumber: string,
    city: string,
    zipCode: string,
    additionalAddressInformation: string,
}

interface CustomerPhoneNumberProps {
    phoneNumber: string,
}

const setInitialAddressData = (initialValues: Customer | null) => ({
    street: initialValues?.street ?? "",
    houseNumber: initialValues?.houseNumber ?? "",
    zipCode: initialValues?.zipCode ?? "",
    city: initialValues?.city ?? "",
    additionalAddressInformation: initialValues?.additionalAddressInformation ?? "",
})

const setInitialPhoneNumberData = (initialValues: Customer | null) => ({
    phoneNumber: initialValues?.phoneNumber ?? ""
})

const setInitialCustomerData = (agencies: AgencyProps[], initialValues: Customer | null) => {
    const agency = agencies.length === 1 ? agencies[0] : null
    if (initialValues === null) return initialCustomer
    return {
        id: initialValues.id,
        firstName: initialValues.firstName,
        lastName: initialValues.lastName,
        email: initialValues.email,
        company: initialValues.company,
        agency
    }
}

const setInitialAgency = (agencies: AgencyProps[], agency: AgencyProps | null | undefined) => {
    if (agency) return agency
    return agencies.length === 1 ? agencies[0] : null
}

const CustomerForm = ({
                          agencies,
                          defaultCustomer,
                          validateForm,
                          showCompany,
                          hideAgencySelection,
                          onFormIsValid,
                          selectedAgency,
                      }: CustomerFormProps) => {
    const [disableAgencySelection, setDisableAgencySelection] = useState(false)
    const [showAdditionalCustomerInformation, setShowAdditionalCustomerInformation] = useState(false)
    const [customer, setCustomer] = useState<CustomCustomerFormProps>(setInitialCustomerData(agencies, defaultCustomer))
    const [addressFormData, setAddressFormData] = useState<CustomerAddressProps>(setInitialAddressData(defaultCustomer))
    const [phoneNumberFormData, setPhoneNumberFormData] = useState<CustomerPhoneNumberProps>(setInitialPhoneNumberData(defaultCustomer))
    const [agency, setAgency] = useState(setInitialAgency(agencies, selectedAgency))
    const [formErrorData, setFormErrorData] = useState<SaveTravelComponentsDialogueFormErrorProps>({
        firstNameError: false,
        lastNameError: false,
        emailError: false,
        companyNameError: false,
        agencyError: false,
    })
    const {
        firstNameError,
        lastNameError,
        emailError,
        agencyError,
        companyNameError,
    } = formErrorData
    const {
        id,
        firstName,
        lastName,
        company,
        email,
    } = customer
    const {
        street,
        houseNumber,
        zipCode,
        city,
        additionalAddressInformation,
    } = addressFormData
    const {phoneNumber} = phoneNumberFormData
    const freelyTypedCustomer = useRef<FreelyTypedCustomerProps | null>(null)
    const isFirstRender = useFirstRender()
    const noAgencyCreated = agencies.length === 0

    useEffect(() => {
        if (!isFirstRender && _anyCustomerInformationEntered()) setDisableAgencySelection(true)
    }, [customer])

    useEffectAllDepsChange(() => {
        if (validateForm > VALIDATE_FORM_STARTING_NUMBER && _validateForm()) onFormIsValid(_createCustomer())
    }, [addressFormData, phoneNumberFormData])

    useEffect(() => {
        if (validateForm > VALIDATE_FORM_STARTING_NUMBER) _validateForm()
    }, [validateForm])

    useEffect(() => {
        if (!isFirstRender) {
            setAgency(selectedAgency ?? null)
            setCustomer(initialCustomer)
        }
    }, [selectedAgency])

    const _createCustomer = () => {
        // must use completeInitialCustomer instead of null because when customer is used in useEffectAllDepsChange()
        // hook, this does not trigger when customer is null. Convertion to null must be done in parent component
        let customerToSave: Customer | null = completeInitialCustomer
        if (!isEqual(initialCustomer, freelyTypedCustomer.current)) {
            const agencyId = agency?.id
            if (typeof agencyId === "number") {
                if (freelyTypedCustomer.current) {
                    customerToSave = {
                        id: freelyTypedCustomer.current.id,
                        firstName: freelyTypedCustomer.current.firstName,
                        lastName: freelyTypedCustomer.current.lastName,
                        email: freelyTypedCustomer.current.email,
                        company: freelyTypedCustomer.current.company,
                        ...addressFormData,
                        ...phoneNumberFormData,
                    }
                } else if (
                    (firstName.length > 0 || lastName.length > 0 || email.length > 0 || (company && company.name.length > 0)) ||
                    id !== null
                ) {
                    customerToSave = {
                        id,
                        firstName,
                        lastName,
                        email,
                        company,
                        ...addressFormData,
                        ...phoneNumberFormData,
                    }
                }
            }
        } else {
            if (
                firstName.length === 0 ||
                lastName.length === 0 ||
                freelyTypedCustomer.current?.firstName.length === 0 ||
                freelyTypedCustomer.current?.lastName.length === 0
            ) {
                // cannot pass  null here, otherwise useEffectAllDepsChange() in parent does not get triggered
                customerToSave = completeInitialCustomer
            } else {
                customerToSave = {
                    id,
                    firstName,
                    lastName,
                    email,
                    company,
                    ...addressFormData,
                    ...phoneNumberFormData,
                }
            }
        }
        return customerToSave
    }

    const _anyCustomerInformationEntered = () => {
        const {firstName, lastName, email, company} = customer
        const customerInformation = [firstName, lastName, email]
        if (company) customerInformation.push(company.name)
        return [customerInformation].some(str => str.length > 0)
    }

    const _validateForm = () => {
        // mandatory
        let validAgency = true
        let validCustomerFirstName = true
        let validCustomerLastName = true
        let validCustomerEmail = true
        let validCompanyName = true
        if (freelyTypedCustomer.current) {
            const {email, firstName, lastName, company} = freelyTypedCustomer.current
            if (email.length > 0) validCustomerEmail = validateEmail(email)
            if (firstName?.length > 0) validCustomerFirstName = validateFirstAndLastName(firstName)
            if (lastName?.length > 0) validCustomerLastName = validateFirstAndLastName(lastName)
            if (showCompany && company?.name) validCompanyName = validateCompanyName(company?.name)
        } else {
            if (email.length > 0) validCustomerEmail = validateEmail(email)
            if (firstName?.length > 0) validCustomerFirstName = validateFirstAndLastName(firstName)
            if (lastName?.length > 0) validCustomerLastName = validateFirstAndLastName(lastName)
            if (showCompany && company?.name) validCompanyName = validateCompanyName(company.name)
        }

        setFormErrorData({
            firstNameError: !validCustomerFirstName,
            lastNameError: !validCustomerLastName,
            emailError: !validCustomerEmail,
            companyNameError: !validCompanyName,
            agencyError: !validAgency,
        })
        return [
            validCustomerFirstName,
            validCustomerLastName,
            validCustomerEmail,
            validAgency
        ].every(Boolean)
    }

    const _updateCustomerOnFreelyTypedString = <K extends keyof FreelyTypedCustomerProps>(key: K, payload: FreelyTypedCustomerProps[K]) => {
        if (freelyTypedCustomer.current) {
            freelyTypedCustomer.current[key] = payload
        } else {
            freelyTypedCustomer.current = {
                firstName,
                lastName,
                email,
                company,
                id: null,
                [key]: payload
            }
        }
    }

    const _updateAddressAndPhoneOnCustomerSelection = (customer: Customer) => {
        setAddressFormData({
            street: customer.street,
            houseNumber: customer.houseNumber,
            zipCode: customer.zipCode,
            city: customer.city,
            additionalAddressInformation: customer.additionalAddressInformation,
        })
        setPhoneNumberFormData({phoneNumber: customer.phoneNumber})
    }

    return <>
        {!hideAgencySelection && agencies.length > 1 && <DropdownInput
            readOnly={disableAgencySelection}
            aria-errormessage={"ERRORS.COMMON.NAME"}
            formError={agencyError}
            required={true}
            label={"GENERAL.AGENCY"}
            containerClasses={"customer-form-agency-dropdown"}
            options={convertAgenciesToDropdownFormat(agencies)}
            setSelection={selection => setAgency(convertAgenciesFromDropdownFormat(selection))}
        />}
        <Fieldset
            showLegend={false}
            legend={'LABELS.CUSTOMER.GENERAL_CUSTOMER_INFORMATION'}
            fieldsetStyles={{position: 'relative'}}
            fieldsetClasses={"form-row"}>
            {(agency == null || noAgencyCreated) &&
                <div className={'save-bundle-create-journey-dialogue-no-agency-customer-overlay'}>
                    <Text
                        classes={'save-bundle-create-journey-dialogue-no-agency-customer-overlay-text'}
                        variant={'headlineSmall'}>{noAgencyCreated ? 'MODALS.SAVE_BUNDLES.CUSTOMER_OVERLAY_NO_AGENCY_CREATED' : 'MODALS.SAVE_BUNDLES.CUSTOMER_OVERLAY_NO_AGENCY_SELECTED'}</Text>
                    <div className={'save-bundle-create-journey-dialogue-no-agency-customer-overlay-background'}></div>
                </div>}
            <AutocompleteQuery
                agencyId={agency?.id ?? null}
                disabled={agency === null}
                containerStyle={{flex: 1}}
                required={false}
                defaultValue={{...customer, ...addressFormData, ...phoneNumberFormData}}
                error={firstNameError}
                queryType={AUTOCOMPLETE_QUERY_TYPE.customerFirstName}
                type={AUTOCOMPLETE_ACTIVITY_TYPE.customer}
                aria-errormessage={"ERRORS.COMMON.NAME"}
                setSelection={value => {
                    if (isCustomer(value)) {
                        setCustomer({
                            ...customer,
                            ...value
                        })
                        _updateAddressAndPhoneOnCustomerSelection(value)
                    }
                }}
                getFreelyTypedString={validateForm}
                placeholder={"GENERAL.FIRST_NAME"}
                label={"GENERAL.CUSTOMER_FIRST_NAME"}
                setFreelyTypedString={value => _updateCustomerOnFreelyTypedString("firstName", value)}
                closeListBoxOnBlur={true}
            />
            <AutocompleteQuery
                agencyId={agency?.id ?? null}
                disabled={agency === null}
                containerStyle={{flex: 1}}
                required={false}
                defaultValue={{...customer, ...addressFormData, ...phoneNumberFormData}}
                error={lastNameError}
                queryType={AUTOCOMPLETE_QUERY_TYPE.customerLastName}
                type={AUTOCOMPLETE_ACTIVITY_TYPE.customer}
                aria-errormessage={"ERRORS.COMMON.NAME"}
                setSelection={value => {
                    if (isCustomer(value)) {
                        setCustomer({
                            ...customer,
                            ...value
                        })
                        _updateAddressAndPhoneOnCustomerSelection(value)
                    }
                }}
                getFreelyTypedString={validateForm}
                placeholder={"GENERAL.LAST_NAME"}
                label={"GENERAL.CUSTOMER_LAST_NAME"}
                setFreelyTypedString={value => _updateCustomerOnFreelyTypedString("lastName", value)}
                closeListBoxOnBlur={true}
            />
            <AutocompleteQuery
                agencyId={agency?.id ?? null}
                disabled={agency === null}
                containerStyle={{flex: 1}}
                required={false}
                defaultValue={{...customer, ...addressFormData, ...phoneNumberFormData}}
                aria-errormessage={"LABELS.COMMON.E_MAIL"}
                queryType={AUTOCOMPLETE_QUERY_TYPE.customerEmail}
                type={AUTOCOMPLETE_ACTIVITY_TYPE.customer}
                error={emailError}
                setSelection={value => {
                    if (isCustomer(value)) {
                        setCustomer({
                            ...customer,
                            ...value
                        })
                        _updateAddressAndPhoneOnCustomerSelection(value)
                    }
                }}
                getFreelyTypedString={validateForm}
                placeholder={"GENERAL.EMAIL"}
                label={"GENERAL.CUSTOMER_EMAIL"}
                setFreelyTypedString={value => _updateCustomerOnFreelyTypedString("email", value)}
                closeListBoxOnBlur={true}
            />
            {showCompany && <AutocompleteQuery
                agencyId={agency?.id ?? null}
                disabled={agency === null}
                containerStyle={{flex: 1}}
                required={false}
                defaultValue={{...customer, ...addressFormData, ...phoneNumberFormData}}
                aria-errormessage={"ERRORS.COMMON.NAME"}
                queryType={AUTOCOMPLETE_QUERY_TYPE.customerCompany}
                type={AUTOCOMPLETE_ACTIVITY_TYPE.customer}
                error={companyNameError}
                setSelection={value => {
                    if (isCustomerCompanyResponse(value)) {
                        setCustomer({
                            ...customer,
                            company: value
                        })
                    }
                }}
                getFreelyTypedString={validateForm}
                placeholder={"GENERAL.NAME"}
                label={"GENERAL.COMPANY_NAME"}
                setFreelyTypedString={value => _updateCustomerOnFreelyTypedString("company", {
                    id: null,
                    name: value
                })}
                closeListBoxOnBlur={true}
            />}
        </Fieldset>
        <Fieldset legend={'LABELS.CUSTOMER.DETAILED_CUSTOMER_INFORMATION'} showLegend={false}>
            <div className={'save-bundle-create-journey-dialogue-additional-customer-information-icon-wrapper'}>
                <IconAndText
                    disabled={agency === null}
                    color={"primary"}
                    containerStyle={{marginBottom: showAdditionalCustomerInformation ? theme.spacer.large : 0}}
                    text={"MODALS.SAVE_BUNDLES.ADDITIONAL_CUSTOMER_INFORMATION"}
                    icon={showAdditionalCustomerInformation ? "caretDown" : "caretRight"}
                    onClick={() => setShowAdditionalCustomerInformation(!showAdditionalCustomerInformation)}/>
            </div>
            <div style={{marginBottom: showAdditionalCustomerInformation ? 2 * theme.spacer.large : "unset"}}
                 className={
                     clsx(
                         "form-row",
                         !showAdditionalCustomerInformation && "save-bundle-create-journey-dialogue-additional-customer-information-visibility",
                     )}>
                <AddressForm
                    defaultValues={{
                        street,
                        houseNumber,
                        zipCode,
                        city,
                        additionalAddressInformation
                    }}
                    showCountry={false}
                    disableForm={false}
                    countryRequired={false}
                    streetRequired={false}
                    houseNumberRequired={false}
                    zipCodeRequired={false}
                    cityRequired={false}
                    additionalAddressInformationRequired={false}
                    validateForm={validateForm}
                    onFormIsValid={setAddressFormData}/>
                <PhoneNumberForm
                    showInternationalPhonePrefix={false}
                    disableForm={false}
                    defaultValues={{phoneNumber}}
                    internationalPhonePrefixRequired={false}
                    phoneNumberRequired={false}
                    validateForm={validateForm}
                    onFormIsValid={setPhoneNumberFormData}/>
            </div>
        </Fieldset>
    </>
}

export default CustomerForm